package nl.volleyplanner.web.controller.planner.planner_configurations

import kotlinx.browser.document
import kotlinx.coroutines.flow.StateFlow
import kotlinx.datetime.LocalTime
import kotlinx.datetime.format
import kotlinx.dom.clear
import kotlinx.html.*
import kotlinx.html.dom.create
import kotlinx.html.js.div
import kotlinx.html.js.span
import nl.volleyplanner.api.planner.*
import nl.volleyplanner.extensions.ifNull
import nl.volleyplanner.sportconfig.NevoboConfig
import nl.volleyplanner.web.ApiDataStateFlow
import nl.volleyplanner.web.VolleyPlannerApplication
import nl.volleyplanner.web.base.HTMLComponent
import nl.volleyplanner.web.base.component.CheckboxType
import nl.volleyplanner.web.base.component.checkbox
import nl.volleyplanner.web.base.component.tooltipAttributes
import nl.volleyplanner.web.base.component.validatingForm
import nl.volleyplanner.web.base.icon
import nl.volleyplanner.web.lib.Tooltip
import nl.volleyplanner.web.util.BootstrapColor
import nl.volleyplanner.web.util.FormUtil
import nl.volleyplanner.web.util.LocalTimeFormatters
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLFormElement
import org.w3c.dom.HTMLInputElement
import org.w3c.dom.get
import org.w3c.xhr.FormData

class PlannerConfigurationComponent(
    val plannerConfigurationsData: ApiDataStateFlow<List<PlannerConstraintConfiguration>?, *>,
    val selectedConfigurationState: StateFlow<PlannerConstraintConfiguration?>
) : HTMLComponent() {
    private val ageGroups = NevoboConfig.ageGroups.values
        .filter { it.ageTo <= 18 }

    override fun onContentReplaced(htmlElement: HTMLElement) {
        super.onContentReplaced(htmlElement)
        createTooltips(htmlElement)
    }

    private fun createTooltips(htmlElement: HTMLElement) {
        val tooltipElements = htmlElement.querySelectorAll("[data-bs-toggle=\"tooltip\"]")
        for (i in 0 until tooltipElements.length) {
            val tooltipElement = tooltipElements[i] as HTMLElement
            Tooltip.Companion.getOrCreateInstance(tooltipElement)
        }
    }

    private fun <T> getConfigurationValue(htmlId: String, parser: (HTMLElement) -> T) : T? {
        val configurationSwitch = document.getElementById("$htmlId-enabled") as HTMLInputElement?
        val inputElement = document.getElementById(htmlId)!!

        if (configurationSwitch != null && !configurationSwitch.checked) return null
        return parser(inputElement as HTMLElement)
    }

    fun getFormConfiguration(configurationData: PlannerConstraintConfiguration, formElement: HTMLFormElement) : PlannerConstraintConfiguration {
        val intParser: (HTMLElement) -> Int = { (it as HTMLInputElement).value.toInt() }
        val checkboxParser: (HTMLElement) -> Boolean = { (it as HTMLInputElement).checked }

        val timeBeforeMatch = getConfigurationValue("time-before-match", intParser)
        val timeAfterMatch = getConfigurationValue("time-after-match", intParser)
        val noTimeReservationOnSameField = getConfigurationValue("no-time-reservation-on-same-field", checkboxParser)!!

        // TODO: migrate inputs above to formdata as well instead of html ids
        val formData = FormData(formElement)
        val balancingModel = PlannerBalancingModel.valueOf(formData.get("planner-balancing-model"))
        val scheduleTeamsAtSameTime = formData.has("schedule-teams-at-same-time")

        return PlannerConstraintConfiguration(
            configurationData.identifier,
            configurationData.name,
            configurationData.slug,
            OwnMatchTimeReservationConstraintConfiguration(
                timeBeforeMatch,
                timeAfterMatch,
                noTimeReservationOnSameField
            ),
            ageGroups.map { ageGroup ->
                AgeGroupConstraintConfiguration(
                    ageGroup.identifier,
                    getConfigurationValue("${ageGroup.identifier}-time-limit", {
                        LocalTimeFormatters.HOUR_MINUTE_FORMAT.parse((it as HTMLInputElement).value)
                    }),
                    getConfigurationValue("${ageGroup.identifier}-max-wait", intParser)
                )
            },
            BalancingConstraintConfiguration(balancingModel, scheduleTeamsAtSameTime)
        )
    }

    suspend fun save(configurationData: PlannerConstraintConfiguration, formElement: HTMLFormElement) {
        if (configurationData != selectedConfigurationState.value) {
            throw IllegalStateException("Selected configuration data changed before saving: [toSave=${configurationData.identifier}, state=${selectedConfigurationState.value?.identifier}]")
        }
        val newConfiguration = getFormConfiguration(configurationData, formElement)
        VolleyPlannerApplication.volleyplannerApiClient
            .organisation
            .plannerConfiguration
            .updatePlannerConfiguration(newConfiguration)
        // TODO: Determine if we should update the selectedConfigurationState with the new configuration
        //  not doing this now because the save() operation sets a confirmation message that is otherwise rerendered
        plannerConfigurationsData.refresh()
    }

    override fun render(consumer: TagConsumer<HTMLElement>): HTMLElement {
        val configurationData = selectedConfigurationState.value ?: return consumer.div { }
        return consumer.div {
            h3 { +configurationData.name }
            validatingForm(
                validSubmitFunction = { event, formElement ->
                    val formElement = (event.currentTarget as HTMLFormElement)
                    save(configurationData, formElement)
                    val submitMessageElement = formElement.querySelector(".vp-form-submit-message")!!
                    submitMessageElement.clear()
                    submitMessageElement.append(document.create.span(classes = "text-success fw-bold") {
                        icon("bi-check-lg")
                        +"Gegevens opgeslagen"
                    })
                }
            ) {
                div(classes = "d-flex sticky-top bg-body p-2 shadow-sm border border-secondary-subtle align-items-center vp-form-group") {
                    button(classes = "btn btn-primary") {
                        type = ButtonType.submit
                        +"Opslaan"
                    }
                    div (classes = "ms-3 vp-form-submit-message") { }
                }
                /**
                 * Standaard regels:
                 *  - Maximaal 1 taak per lid per thuisdag
                 *  - Geen taken als lid zelf geen wedstrijd heeft
                 *  - Geen taken tijdens eigen wedstrijd
                 *  - Scheidsrechters/Tellers alleen uit de geconfigureerde groepen voor het niveau
                 *  - Taken worden evenredig verdeeld nav:
                 *      - aantal wedstrijden dat het lid zelf speelt
                 *      - vrijwilligersscore (indien hier gebruik van wordt gemaakt)
                 *  - Voor zover mogelijk worden getracht maximaal de helft van een team in te plannen voor taken op een dag
                 *      - Zodoende er ruimte blijft om binnen het eigen team te ruilen of voor andere taken als bv zaalwacht
                 *  - Leden met ongunstigere schemas uit vorige planningen worden ontzien
                 */
                div(classes = "border border-secondary rounded p-2 my-3 vp-form-group") {
                    h3 { +"Standaard regels" }
                    p(classes = "lead") { +"De planner houdt altijd rekening met deze regels. Deze zijn momenteel niet uit te schakelen of aan te passen." }
                    // TODO: Support max 1 taak per speelweek
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true,
                        label = "Maximaal 1 taak per lid per thuisdag"
                    )
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true,
                        label = "Leden niet inplannen als ze zelf geen thuiswedstrijd hebben"
                    )
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true,
                        label = "Leden niet inplannen tijdens hun eigen wedstrijd"
                    )
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true,
                        label = "Scheidsrechters/tellers alleen inplannen vanuit de geconfigureerde groepen"
                    )
                    // TODO: Info tooltip
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true
                    ) {
                        +"Wachttijd tussen een taak en eigen wedstrijd minimaliseren "
                        span {
                            tooltipAttributes(
                                titleLabel = "De planner probeert leden zo dicht mogelijk op hun eigen wedstrijd in te plannen. " +
                                        "Pas als leden anders teveel taken toegewezen krijgen, of er bv geen scheidsrechter beschikbaar is " +
                                        "kan het voorkomen dat iemand wat langer moet wachten."
                            )
                            icon("bi-info-circle", color = BootstrapColor.Info)
                        }
                    }
                    // TODO: Info tooltip
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true
                    ) {
                        +"Taken evenredig verdelen over leden "
                        span {
                            tooltipAttributes(
                                titleLabel = "De probeert taken eerlijk te verdelen over alle individuele leden. " +
                                        "Hierbij wordt oa ook de vrijwilligersscore en het aantal wedstrijden dat een " +
                                        "lid zelf thuis speelt meegewogen."
                            )
                            icon("bi-info-circle", color = BootstrapColor.Info)
                        }
                    }
                    // TODO: Info tooltip
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true
                    ) {
                        +"Waar mogelijk, maximaal de helft van een team op dezelfde dag inplannen "
                        span {
                            tooltipAttributes(
                                titleLabel = "Om het makkelijker te maken om binnen het eigen team taken te ruilen als iemand niet kan " +
                                        "en om evt mensen uit een team beschikbaar te houden voor een zaaldienst probeert de planner " +
                                        "maximaal de helft van een team op eenzelfde speeldag in te plannen."
                            )
                            icon("bi-info-circle", color = BootstrapColor.Info)
                        }
                    }
                    // TODO: Info tooltip
                    checkbox(
                        CheckboxType.Switch, isEnabled = false, isChecked = true
                    ) {
                        +"Leden met ongunstigere schemas uit eerdere planningen ontzien "
                        span {
                            tooltipAttributes(
                                titleLabel = "Moest een lid een keer extra vroeg op? Of kwam iemand een keertje vaker aan de beurt dan de rest? " +
                                        "Ongunstige scenarios worden onthouden zodat deze leden de volgende keer niet weer de sjaak zijn. "
                            )
                            icon("bi-info-circle", color = BootstrapColor.Info)
                        }
                    }
                }

                div(classes = "border border-secondary rounded p-2 my-3 vp-form-group") {
                    h3 { +"Verdeling taken" }
                    p(classes = "lead") { +"Instellingen voor het toewijzen van taken en het houden van balans." }

                    val balancingConfiguration = configurationData.balancingConstraintConfiguration
                    div {
                        checkbox(
                            CheckboxType.Radio,
                            isEnabled = true,
                            isChecked = balancingConfiguration.balancingModel == PlannerBalancingModel.FictiveWorkloadSpread,
                            htmlId = "planner-balancing-model-fictive-workload-spread",
                            htmlName = "planner-balancing-model",
                            htmlValue = PlannerBalancingModel.FictiveWorkloadSpread.name,
                            //onChangeFunction = toggleInputEnabled("time-before-match")
                            label = "Oude model (op basis van aantal taken)"
                        )
                        checkbox(
                            CheckboxType.Radio,
                            isEnabled = true,
                            isChecked = balancingConfiguration.balancingModel == PlannerBalancingModel.MatchProportionalTaskLoad,
                            htmlId = "planner-balancing-model-match-proportional-task-load",
                            htmlName = "planner-balancing-model",
                            htmlValue = PlannerBalancingModel.MatchProportionalTaskLoad.name,
//                            onChangeFunction = { event ->
//                                val inputElement = event.currentTarget as HTMLInputElement
////                                val proportional
////                                if (inputElement.checked) {
////
////                                } else {
////
////                                }
//                            },
                            label = "Nieuwe model (taken relatief aan wedstrijden)"
                        )
                    }
                    // TODO: enable configuration of %tasks per amount of games
//                    div {
//                        id = "vp-match-proportional-task-load-factor-container"
//                        if (balancingConfiguration.balancingModel != PlannerBalancingModel.MatchProportionalTaskLoad) {
//                            classes += "d-none"
//                        }
//
//                        label(classes = "form-label") {
//                            +""
//                        }
//                        input(classes = "form-range") {
//                            id = "vp-match-proportional-task-load-factor-selector"
//
//                            type = InputType.range
//                            min = "0"
//                            max = "100"
//                            step = "1"
//                            //value = "50"
//                        }
//                        span {
//                            id = "vp-match-proportional-task-load-factor-message"
//                            +"33% van wedstrijden als taak (33% van 9 wedstrijden = 3 taken)"
//                        }
//                    }
                    checkbox(
                        CheckboxType.Switch,
                        isEnabled = true,
                        isChecked = balancingConfiguration.scheduleTeamsAtSameTime,
                        htmlId = "schedule-teams-at-same-time",
                        htmlName = "schedule-teams-at-same-time",
                        label = "Plan leden uit een team zoveel mogelijk op hetzelfde moment"
                    )
                }

                /**
                 *  Ruimte voor/na eigen wedstrijd reserveren
                 *   - min voor eigen wedstrijd
                 *   - min na eigen wedstrijd
                 *   - ook als wedstrijd op zelfde veld is?
                 *
                 *  Note: planner gaat uit van 120 minuten per wedstrijd. En 75 per CMV wedstrijd
                 */
                div(classes = "border border-secondary rounded p-2 mb-3 vp-form-group") {
                    h3 { +"Ruimte voor/na eigen wedstrijd" }
                    div (classes = "row flex-lg-row-reverse") {
                        div (classes = "col-12 col-lg-6 text-center") {
                            figure {
                                img(
                                    src = "/img/web/infographic-reservering-eigen-wedstrijd.png",
                                    classes = "img-fluid border rounded-3",
                                    alt = "Screenshot met scenario waar een taak van twee tot vier uur loopt. " +
                                            "Een wedstrijd van heren 3 om 16:30 begint, en met 20 minuten reservering niet overlapt. " +
                                            "En een wedstrijd van dames 4 die om 16:00 begint en met 20 minuten reservering wel overlapt."
                                ) {
                                    style = "width: 100%; max-width: 450px;"
                                }
                                figcaption(classes = "fw-small text-secondary") {
                                    +"Voorbeeld: Bij een taak van 14:00 - 16:00 worden leden die om 16:30 moeten spelen (H3) wel ingepland "
                                    +"bij een tijdsreservering. Maar leden die om 16:00 spelen (D4) niet."
                                }
                            }
                        }
                        div (classes = "col-12 col-lg-6") {
                            p(classes = "lead") {
                                +"De planner gaat standaard uit van een speelduur van: "
                                ul {
                                    li { +"120 minuten voor alle senioren en jeugdwedstrijden" }
                                    li { +"60 minuten voor alle CMV wedstrijden" }
                                }
                            }
                            p (classes = "small") {
                                +"Alle tijden gerekend vanaf de aanvangstijd als aangegeven door de Nevobo."
                            }
                            p (classes = "small") {
                                +"Leden worden nooit ingepland wanneer een taak zou overlappen met hun eigen wedstrijd. "
                                br {}
                                unsafe {+"Eventueel kan je hier extra ruimte reserveren voor/na de <strong>eigen wedstrijd</strong> van een lid. Bijvoorbeeld 20 minuten inspeeltijd."}
                            }
                            val reservationConfiguration = configurationData.ownMatchTimeReservationConstraintConfiguration
                            div(classes = "row mt-2 mb-2 align-items-center") {
                                div(classes = "col-auto") {
                                    checkbox(
                                        CheckboxType.Switch,
                                        isEnabled = true,
                                        isChecked = reservationConfiguration.timeReservationBeforeMatchMinutes != null,
                                        htmlId = "time-before-match-enabled",
                                        onChangeFunction = FormUtil.toggleInputEnabled("time-before-match")
                                    )
                                }
                                div(classes = "col-auto") {
                                    label {
                                        htmlFor = "time-before-match"
                                        +"Voor eigen wedstrijd"
                                    }
                                    div(classes = "input-group has-validation") {
                                        input(classes = "form-control") {
                                            id = "time-before-match"
                                            type = InputType.number
                                            min = "0"
                                            max = "720" // 12 hours should be sufficient
                                            disabled = reservationConfiguration.timeReservationBeforeMatchMinutes == null
                                            value = reservationConfiguration.timeReservationBeforeMatchMinutes.ifNull { 20 }
                                                .toString()
                                        }
                                        span(classes = "input-group-text") { +"minuten" }
                                        div (classes = "invalid-feedback") {
                                            +"Vereist waarde tussen 0 en 720 minuten"
                                        }
                                    }
                                }
                            }
                            div(classes = "row mb-2 align-items-center") {
                                div(classes = "col-auto") {
                                    checkbox(
                                        CheckboxType.Switch,
                                        isEnabled = true,
                                        isChecked = reservationConfiguration.timeReservationAfterMatchMinutes != null,
                                        htmlId = "time-after-match-enabled",
                                        onChangeFunction = FormUtil.toggleInputEnabled("time-after-match")
                                    )
                                }
                                div(classes = "col-auto") {
                                    label {
                                        htmlFor = "time-after-match"
                                        +"Na eigen wedstrijd"
                                    }
                                    div(classes = "input-group has-validation") {
                                        input(classes = "form-control") {
                                            id = "time-after-match"
                                            type = InputType.number
                                            min = "0"
                                            max = "720" // 12 hours should be sufficient
                                            disabled = reservationConfiguration.timeReservationAfterMatchMinutes == null
                                            value = reservationConfiguration.timeReservationAfterMatchMinutes.ifNull { 0 }
                                                .toString()
                                        }
                                        span(classes = "input-group-text") { +"minuten" }
                                        div (classes = "invalid-feedback") {
                                            +"Vereist waarde tussen 0 en 720 minuten"
                                        }
                                    }
                                }
                            }
                            div(classes = "row align-items-center") {
                                div (classes = "col") {
                                    checkbox(
                                        CheckboxType.Switch,
                                        isEnabled = true,
                                        isChecked = reservationConfiguration.disableTimeReservationOnSameField,
                                        htmlId = "no-time-reservation-on-same-field",
                                        label = "Niet reserveren als de eigen wedstrijd op hetzelfde veld plaatsvind"
                                    )
                                }
                            }
                        }
                    }
                }

                /**
                 * No late tasks for youth:
                 *  - per age group
                 *  - uiterlijk tijdstip
                 *  - maximum wachttijd na wedstrijd
                 */
                div(classes = "border border-secondary rounded p-2 mb-3 vp-form-group") {
                    h3 { +"Jeugd" }
                    p(classes = "lead") {
                        +"Hoewel de planner de wachttijd met een eigen wedstrijd standaard al minimaliseert kunnen ongunstige scenarios niet uitgesloten worden. "
                        +"Voor de jeugd kunnen per leeftijdsgroep extra limieten worden aangegeven. "
                    }
                    div {
                        ageGroups
                            .sortedDescending()
                            .forEach { ageGroup ->
                                val ageGroupConfiguration =
                                    configurationData.ageGroupConstraints.find { it.ageGroupIdentifier == ageGroup.identifier }
                                p(classes = "fw-bold") { +ageGroup.label }
                                div(classes = "row mb-2 align-items-center") {
                                    div(classes = "col-auto") {
                                        checkbox(
                                            CheckboxType.Switch,
                                            isEnabled = true,
                                            isChecked = ageGroupConfiguration?.ageGroupTimeLimit != null,
                                            htmlId = "${ageGroup.identifier}-time-limit-enabled",
                                            onChangeFunction = FormUtil.toggleInputEnabled("${ageGroup.identifier}-time-limit")
                                        )
                                    }
                                    div(classes = "col-auto") {
                                        label {
                                            htmlFor = "${ageGroup.identifier}-time-limit"
                                            +"Niet meer inplannen na "
                                            span {
                                                tooltipAttributes(
                                                    titleLabel = "Gebaseerd op de aanvangstijd van de wedstrijd. Is de limiet 17:00 en begint de wedstrijd om 16:45, dan zal het lid nog wel worden ingepland."
                                                )
                                                icon("bi-info-circle", color = BootstrapColor.Info)
                                            }
                                        }
                                        div(classes = "input-group has-validation") {
                                            input(classes = "form-control") {
                                                type = InputType.time
                                                disabled = ageGroupConfiguration?.ageGroupTimeLimit == null
                                                id = "${ageGroup.identifier}-time-limit"
                                                min = "10:00"
                                                max = "21:00"
                                                value = ageGroupConfiguration?.ageGroupTimeLimit
                                                    .ifNull { LocalTime(16, 0) }
                                                    .format(LocalTimeFormatters.HOUR_MINUTE_FORMAT)
                                            }
                                            span(classes = "input-group-text") { +"uur" }
                                            div (classes = "invalid-feedback") {
                                                +"Vereist waarde tussen 10:00 en 21:00 uur"
                                            }
                                        }
                                    }
                                }
                                div(classes = "row mb-2 align-items-center") {
                                    div(classes = "col-auto") {
                                        checkbox(
                                            CheckboxType.Switch,
                                            isEnabled = true,
                                            isChecked = ageGroupConfiguration?.ageGroupMaxWaitTimeMinutes != null,
                                            htmlId = "${ageGroup.identifier}-max-wait-enabled",
                                            onChangeFunction = FormUtil.toggleInputEnabled("${ageGroup.identifier}-max-wait")
                                        )
                                    }
                                    div(classes = "col-auto") {
                                        label(classes = "form-label") {
                                            htmlFor = "${ageGroup.identifier}-max-wait"
                                            +"Max. wachttijd tussen taak en eigen wedstrijd "
                                            span {
                                                tooltipAttributes(
                                                    titleLabel = "Gerekend vanaf de officiele aanvangstijden/eindtijden met de eigen wedstrijd. "
                                                )
                                                icon("bi-info-circle", color = BootstrapColor.Info)
                                            }
                                        }
                                        div(classes = "input-group") {
                                            input(classes = "form-control has-validation") {
                                                type = InputType.number
                                                disabled = ageGroupConfiguration?.ageGroupMaxWaitTimeMinutes == null
                                                id = "${ageGroup.identifier}-max-wait"
                                                min = "0"
                                                max = "720" // 12 hours should be sufficient
                                                value = ageGroupConfiguration?.ageGroupMaxWaitTimeMinutes.ifNull { 120 }
                                                    .toString()
                                            }
                                            span(classes = "input-group-text") { +"minuten" }
                                            div (classes = "invalid-feedback") {
                                                +"Vereist waarde tussen 0 en 720 minuten"
                                            }
                                        }
                                    }
                                }
                            }
                    }
                }
            }
        }
    }

    override suspend fun getState() = selectedConfigurationState
}